import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Radio, Volume2, VolumeX, Signal, Antenna, Lock, Unlock, Wifi, Zap } from 'lucide-react';
import { ITEMS } from '@/config/constants';
import LeonDialogue from '@/components/LeonDialogue';
import CONFIG from '@/config/siteConfig';
import { RADIO_TUNING } from '@/config/missionContent';

const TARGET_FREQ = 87.3;
const MESSAGE_LINES = [
  `...${CONFIG.recipientName}... can you hear me?`,
  'This is Leon. If you\'re receiving this...',
  `...then you're closer than anyone has ever been.`,
  'The vault is just ahead.',
  `Everything beyond this point... was made for you.`,
  `${CONFIG.senderName} wanted you to know...`,
  `...you are the mission. You always were.`,
  'Signal Beacon locked. Proceed to the final checkpoint.',
];

/* ── AmbientBackground ─────────────────────────────────── */
function AmbientBackground({ signalStrength, locked }) {
  const dots = useMemo(() =>
    Array.from({ length: 20 }, (_, i) => ({
      id: i,
      left: `${10 + (i * 4.1) % 80}%`,
      top: `${10 + (i * 4.7 + 3) % 80}%`,
      dur: 3 + (i % 3),
      delay: (i * 0.37) % 2,
    })), []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {/* Base gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-950 to-black" />

      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,255,65,0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,255,65,0.5) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }}
      />

      {/* Radar sweep */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
          className="w-[600px] h-[600px] relative"
        >
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: `conic-gradient(from 0deg, transparent 0deg, ${locked ? 'rgba(34,197,94,0.08)' : 'rgba(0,255,65,0.05)'} 60deg, transparent 120deg)`
            }}
          />
        </motion.div>

        {/* Radar circles */}
        {[150, 220, 290].map((size, i) => (
          <motion.div
            key={i}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border"
            style={{
              width: size,
              height: size,
              borderColor: locked ? 'rgba(34,197,94,0.15)' : 'rgba(0,255,65,0.08)'
            }}
            animate={{ scale: [1, 1.02, 1], opacity: [0.5, 0.8, 0.5] }}
            transition={{ duration: 3, delay: i * 0.5, repeat: Infinity }}
          />
        ))}
      </div>

      {/* Floating dots (memoized positions) */}
      {dots.map(d => (
        <motion.div
          key={d.id}
          className="absolute w-1 h-1 rounded-full bg-green-500/30"
          style={{ left: d.left, top: d.top }}
          animate={{
            y: [0, -30, 0],
            opacity: [0, 0.6 * signalStrength, 0],
          }}
          transition={{
            duration: d.dur,
            delay: d.delay,
            repeat: Infinity,
          }}
        />
      ))}

      {/* Green pulsing vignette */}
      <motion.div
        className="absolute inset-0"
        animate={{ opacity: [0.15, 0.3, 0.15] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,40,10,0.6) 100%)' }}
      />

      {/* CRT scanlines */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,65,0.08) 2px, rgba(0,255,65,0.08) 4px)',
          backgroundSize: '100% 4px',
        }}
      />

      {/* Top scan line */}
      <motion.div
        animate={{ x: ['-100%', '100%'] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
        className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-500/30 to-transparent"
      />
    </div>
  );
}

/* ── RadioTutorial ─────────────────────────────────────── */
function RadioTutorial({ onStart }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="relative z-10 max-w-md mx-auto"
    >
      <div className="bg-black/70 border border-green-800/40 rounded-lg p-6 backdrop-blur-sm">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-green-900/40 border border-green-600/30 flex items-center justify-center">
            <Radio size={20} className="text-green-400" />
          </div>
          <div>
            <h3 className="font-orbitron text-green-400 text-sm tracking-wider">FREQUENCY PROTOCOL</h3>
            <p className="text-xs font-terminal text-green-700 tracking-widest mt-0.5">SECURE CHANNEL ACQUISITION</p>
          </div>
        </div>

        {/* Procedure */}
        <div className="mb-5">
          <p className="text-xs font-terminal text-green-600 tracking-[3px] mb-3">PROCEDURE</p>
          <div className="space-y-2.5">
            {[
              { n: '01', text: 'Adjust the FREQUENCY DIAL to locate the signal' },
              { n: '02', text: 'Watch the WAVEFORM and SIGNAL METER for strength' },
              { n: '03', text: 'LOCK the signal when the waveform stabilizes' },
            ].map(step => (
              <div key={step.n} className="flex items-start gap-3">
                <span className="text-xs font-orbitron text-green-500 mt-0.5">{step.n}</span>
                <p className="text-xs text-gray-300 font-rajdhani">{step.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Intel */}
        <div className="mb-5">
          <p className="text-xs font-terminal text-green-600 tracking-[3px] mb-3">INTEL</p>
          <div className="flex items-center gap-3 bg-green-950/30 border border-green-800/30 rounded px-4 py-2.5">
            <Signal size={14} className="text-green-400 shrink-0" />
            <div>
              <span className="text-xs font-terminal text-green-600 tracking-wider">TARGET FREQUENCY</span>
              <p className="font-orbitron text-green-400 text-lg tracking-wider" style={{ textShadow: '0 0 10px rgba(34,197,94,0.4)' }}>UNKNOWN</p>
            </div>
          </div>
        </div>

        {/* Warning */}
        <div className="flex items-center gap-2 bg-yellow-950/20 border border-yellow-900/30 rounded px-3 py-2 mb-5">
          <Antenna size={12} className="text-yellow-400 shrink-0" />
          <span className="text-xs font-terminal text-yellow-400 tracking-wider">SIGNAL NOISE INCREASES AT WRONG FREQUENCIES</span>
        </div>

        {/* Start button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onStart}
          className="w-full py-3 rounded font-orbitron text-sm tracking-widest text-black relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #22c55e, #00ff41)' }}
        >
          <span className="relative z-10">BEGIN SCAN</span>
          <motion.div
            className="absolute inset-0"
            animate={{ x: ['-100%', '200%'] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)', width: '50%' }}
          />
        </motion.button>
      </div>
    </motion.div>
  );
}

/* ── RadioHUD ──────────────────────────────────────────── */
function RadioHUD({ signalStrength, locked, isClose }) {
  return (
    <div className="max-w-3xl mx-auto mb-4 px-4 relative z-10">
      <div className="flex items-center justify-between bg-black/40 border border-green-900/30 rounded px-4 py-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-terminal text-green-700 tracking-wider">CHECKPOINT 10 // RADIO CONTACT</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <Signal size={10} className="text-green-500" />
            <span className="text-xs font-orbitron text-green-400">{Math.round(signalStrength * 100)}%</span>
          </div>
          <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-terminal tracking-wider ${
            locked
              ? 'bg-green-500/20 text-green-400'
              : isClose
                ? 'bg-yellow-500/20 text-yellow-400'
                : 'bg-red-500/20 text-red-400'
          }`}>
            <div className={`w-1.5 h-1.5 rounded-full ${locked ? 'bg-green-400' : isClose ? 'bg-yellow-400' : 'bg-red-400'}`} />
            {locked ? 'LOCKED' : isClose ? 'DETECTED' : 'NO SIGNAL'}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── WaveformDisplay ───────────────────────────────────── */
function WaveformDisplay({ signalStrength, locked }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;

    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
      ctx.fillRect(0, 0, w, h);

      timeRef.current += 0.05;
      const t = timeRef.current;

      // Grid lines
      ctx.strokeStyle = 'rgba(0, 255, 65, 0.1)';
      ctx.lineWidth = 0.5;

      for (let y = 0; y <= h; y += h / 6) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }

      for (let x = 0; x <= w; x += w / 12) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }

      // Static noise
      const noiseCount = Math.floor(100 * (1 - signalStrength));
      ctx.fillStyle = 'rgba(0, 255, 65, 0.15)';
      for (let i = 0; i < noiseCount; i++) {
        ctx.fillRect(
          Math.random() * w,
          Math.random() * h,
          Math.random() * 3 + 1,
          1
        );
      }

      // Wave color
      const r = locked ? 34 : signalStrength > 0.7 ? 74 : signalStrength > 0.4 ? 234 : 239;
      const g = locked ? 197 : signalStrength > 0.7 ? 222 : signalStrength > 0.4 ? 179 : 68;
      const b = locked ? 94 : signalStrength > 0.7 ? 128 : signalStrength > 0.4 ? 8 : 68;

      // Glow wave
      ctx.beginPath();
      ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 0.2)`;
      ctx.lineWidth = 20;
      ctx.lineCap = 'round';

      for (let x = 0; x < w; x += 2) {
        const noise = (1 - signalStrength) * (Math.random() - 0.5) * 40;
        const wave = Math.sin((x / w) * Math.PI * 4 + t) * (20 + signalStrength * 40);
        const y = h / 2 + wave + noise;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();

      // Main wave
      ctx.beginPath();
      ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${0.5 + signalStrength * 0.5})`;
      ctx.lineWidth = 3;
      ctx.shadowColor = `rgba(${r}, ${g}, ${b}, 0.8)`;
      ctx.shadowBlur = 15;

      for (let x = 0; x < w; x += 2) {
        const noise = (1 - signalStrength) * (Math.random() - 0.5) * 40;
        const wave = Math.sin((x / w) * Math.PI * 4 + t) * (20 + signalStrength * 40);
        const y = h / 2 + wave + noise;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Center line
      ctx.strokeStyle = 'rgba(0, 255, 65, 0.1)';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(0, h / 2);
      ctx.lineTo(w, h / 2);
      ctx.stroke();
      ctx.setLineDash([]);

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [signalStrength, locked]);

  return (
    <div className="relative rounded-2xl overflow-hidden bg-black border-2 border-green-900/30"
      style={{ boxShadow: 'inset 0 0 50px rgba(0,0,0,0.8)' }}>
      <canvas ref={canvasRef} width={600} height={150} className="w-full h-auto" />

      <div className="absolute top-2 left-3 flex items-center gap-2">
        <motion.div
          animate={{ opacity: [1, 0.4, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className={`w-2 h-2 rounded-full ${locked ? 'bg-green-400' : 'bg-red-400'}`}
        />
        <span className="text-xs font-mono text-green-600 tracking-widest">WAVEFORM</span>
      </div>

      <div className="absolute top-2 right-3 text-xs font-mono text-green-700">
        {locked ? 'SIGNAL LOCKED' : 'SCANNING...'}
      </div>

      {/* CRT effect */}
      <div className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)'
        }}
      />
    </div>
  );
}

/* ── AudioVisualizer ───────────────────────────────────── */
function AudioVisualizer({ signalStrength, locked }) {
  const bars = 32;

  return (
    <div className="flex items-end justify-center gap-[2px] h-16 px-4">
      {Array.from({ length: bars }).map((_, i) => {
        const centerDist = Math.abs(i - bars / 2) / (bars / 2);
        const baseHeight = (1 - centerDist * 0.5) * signalStrength;

        return (
          <motion.div
            key={i}
            className="w-[6px] rounded-t-sm"
            animate={{
              height: `${Math.max(8, baseHeight * 100)}%`,
              opacity: 0.4 + signalStrength * 0.6,
            }}
            transition={{
              duration: 0.15,
              delay: Math.abs(i - bars / 2) * 0.01,
            }}
            style={{
              background: locked
                ? 'linear-gradient(to top, #22c55e, #4ade80)'
                : signalStrength > 0.7
                  ? 'linear-gradient(to top, #22c55e, #86efac)'
                  : signalStrength > 0.4
                    ? 'linear-gradient(to top, #eab308, #fde047)'
                    : 'linear-gradient(to top, #ef4444, #fca5a5)',
              boxShadow: signalStrength > 0.5 ? '0 0 10px rgba(34,197,94,0.3)' : 'none'
            }}
          />
        );
      })}
    </div>
  );
}

/* ── FrequencyDisplay ──────────────────────────────────── */
function FrequencyDisplay({ frequency, locked, isClose }) {
  return (
    <div className="text-center">
      <div className="bg-black/80 rounded-xl p-6 border-2 border-green-900/40 inline-block"
        style={{ boxShadow: 'inset 0 0 30px rgba(0,0,0,0.5)' }}>
        <motion.div
          animate={!locked ? { opacity: [1, 0.85, 1] } : {}}
          transition={{ duration: 0.1, repeat: Infinity, repeatDelay: 1.5 }}
          className="font-mono text-6xl md:text-7xl font-bold tracking-wider"
          style={{
            color: locked ? '#22c55e' : isClose ? '#4ade80' : '#00ff41',
            textShadow: locked
              ? '0 0 40px rgba(34,197,94,0.8), 0 0 80px rgba(34,197,94,0.4)'
              : '0 0 30px rgba(0,255,65,0.6), 0 0 60px rgba(0,255,65,0.3)',
          }}
        >
          {frequency.toFixed(1)}
        </motion.div>
        <div className="text-green-600 text-lg font-mono tracking-[0.5em] mt-1">MHz</div>
      </div>

      <motion.div
        animate={!locked && isClose ? { scale: [1, 1.05, 1] } : {}}
        transition={{ duration: 0.8, repeat: Infinity }}
        className={`mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-mono tracking-wider border ${
          locked
            ? 'bg-green-500/20 border-green-500/50 text-green-400'
            : isClose
              ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400'
              : 'bg-red-500/20 border-red-500/50 text-red-400'
        }`}
      >
        <div className={`w-2 h-2 rounded-full ${locked ? 'bg-green-400' : isClose ? 'bg-yellow-400' : 'bg-red-400'}`} />
        {locked ? 'LOCKED' : isClose ? 'SIGNAL DETECTED' : 'NO SIGNAL'}
      </motion.div>
    </div>
  );
}

/* ── SignalMeter ────────────────────────────────────────── */
function SignalMeter({ strength, locked }) {
  const segments = 10;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="text-xs font-mono text-green-700 tracking-widest">SIGNAL STRENGTH</div>

      <div className="flex items-end gap-1">
        {Array.from({ length: segments }).map((_, i) => {
          const threshold = (i + 1) / segments;
          const isActive = strength >= threshold;
          const isHigh = i >= segments * 0.7;
          const isMid = i >= segments * 0.4 && i < segments * 0.7;

          return (
            <motion.div
              key={i}
              animate={isActive ? { opacity: [0.7, 1, 0.7] } : { opacity: 0.2 }}
              transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.05 }}
              className="w-3 rounded-sm"
              style={{
                height: `${16 + i * 4}px`,
                background: !isActive
                  ? '#1f2937'
                  : locked
                    ? '#22c55e'
                    : isHigh
                      ? '#22c55e'
                      : isMid
                        ? '#eab308'
                        : '#ef4444',
                boxShadow: isActive ? `0 0 8px ${locked || isHigh ? 'rgba(34,197,94,0.5)' : isMid ? 'rgba(234,179,8,0.5)' : 'rgba(239,68,68,0.5)'}` : 'none'
              }}
            />
          );
        })}
      </div>

      <div className="text-2xl font-mono font-bold text-green-400">
        {Math.round(strength * 100)}%
      </div>
    </div>
  );
}

/* ── TuningDial ────────────────────────────────────────── */
function TuningDial({ frequency, onChange, disabled }) {
  const handleChange = (e) => {
    if (!disabled) {
      onChange(Number(e.target.value));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-xs font-mono text-green-700">
        <span>FREQUENCY CONTROL</span>
        <span className="text-green-400">{frequency.toFixed(1)} MHz</span>
      </div>

      <div className="relative">
        <div className="h-4 rounded-full overflow-hidden"
          style={{
            background: 'rgba(34,197,94,0.15)',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)'
          }}
        />
        <input
          type="range"
          min="80"
          max="108"
          step="0.1"
          value={frequency}
          onChange={handleChange}
          disabled={disabled}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white border-4 border-green-500 pointer-events-none"
          style={{
            left: `calc(${((frequency - 80) / 28) * 100}% - 12px)`,
            boxShadow: '0 0 15px rgba(34,197,94,0.5), 0 2px 8px rgba(0,0,0,0.3)'
          }}
        />
      </div>

      <div className="flex justify-between text-xs font-mono text-green-800">
        <span>80</span>
        <span className="text-green-800">MHz</span>
        <span>108</span>
      </div>
    </div>
  );
}

/* ── Main Component ────────────────────────────────────── */
export default function RadioTuning({ onComplete, onItemCollected }) {
  const [showLeon, setShowLeon] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [active, setActive] = useState(false);
  const [frequency, setFrequency] = useState(94.0);
  const [locked, setLocked] = useState(false);
  const [revealedLines, setRevealedLines] = useState(0);
  const [solved, setSolved] = useState(false);
  const [showFinalLeon, setShowFinalLeon] = useState(false);
  const [successPhase, setSuccessPhase] = useState(0);
  const [showProceed, setShowProceed] = useState(false);

  const signalStrength = Math.max(0, 1 - Math.abs(frequency - TARGET_FREQ) / 12);
  const isClose = Math.abs(frequency - TARGET_FREQ) <= 2;
  const canLock = Math.abs(frequency - TARGET_FREQ) <= 0.5;

  /* Mount-reset */
  useEffect(() => {
    setShowLeon(true);
    setShowTutorial(false);
    setActive(false);
    setFrequency(94.0);
    setLocked(false);
    setRevealedLines(0);
    setSolved(false);
    setShowFinalLeon(false);
    setSuccessPhase(0);
    setShowProceed(false);
  }, []);

  const handleLeonDismiss = useCallback(() => {
    setShowLeon(false);
    setShowTutorial(true);
  }, []);

  const handleTutorialStart = useCallback(() => {
    setShowTutorial(false);
    setActive(true);
  }, []);

  const handleLock = useCallback(() => {
    if (!canLock || locked) return;
    setLocked(true);
    setFrequency(TARGET_FREQ);
  }, [canLock, locked]);

  // Reveal message lines
  useEffect(() => {
    if (!locked || revealedLines >= MESSAGE_LINES.length) return;
    const timer = setTimeout(() => setRevealedLines(prev => prev + 1), 500);
    return () => clearTimeout(timer);
  }, [locked, revealedLines]);

  // Proceed from transmission → multi-phase success
  const handleProceed = useCallback(() => {
    if (solved) return;
    setSolved(true);
    setActive(false);
    setSuccessPhase(1);
    setTimeout(() => setSuccessPhase(2), 500);
    setTimeout(() => setSuccessPhase(3), 1200);
    setTimeout(() => setSuccessPhase(4), 1800);
    setTimeout(() => setShowFinalLeon(true), 2200);
  }, [solved]);

  const handleFinalDismiss = useCallback(() => {
    setShowFinalLeon(false);
    onItemCollected?.(ITEMS.SIGNAL_BEACON);
    setShowProceed(true);
  }, [onItemCollected]);

  return (
    <div className="puzzle-container relative min-h-screen" data-testid="puzzle-radio-tuning">
      <AmbientBackground signalStrength={signalStrength} locked={locked} />

      {/* Header */}
      <div className="relative z-10 text-center pt-8 pb-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-3 mb-3"
        >
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}>
            <Antenna size={16} className="text-green-500" />
          </motion.div>
          <span className="text-xs font-mono tracking-[0.4em] text-green-600">CHECKPOINT 10</span>
          <motion.div animate={{ rotate: -360 }} transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}>
            <Wifi size={16} className="text-green-500" />
          </motion.div>
        </motion.div>

        <h1 className="text-4xl md:text-5xl font-bold tracking-[0.2em] text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-green-300 to-green-400 mb-2"
          style={{ fontFamily: 'Orbitron, monospace' }}>
          RADIO CONTACT
        </h1>
        <p className="text-green-700 font-mono text-sm tracking-wider">
          Locate Leon's Transmission Signal
        </p>
      </div>

      {/* Tutorial */}
      <AnimatePresence>
        {showTutorial && <RadioTutorial onStart={handleTutorialStart} />}
      </AnimatePresence>

      {/* HUD */}
      {(active || locked || solved) && (
        <RadioHUD signalStrength={signalStrength} locked={locked} isClose={isClose} />
      )}

      {/* Main content */}
      {(active || locked || solved) && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 max-w-3xl mx-auto px-4 space-y-6"
        >
          {/* Radio unit */}
          <div className="bg-gradient-to-b from-gray-900/95 to-black/95 rounded-3xl p-6 md:p-8 border border-green-900/30"
            style={{ boxShadow: '0 0 80px rgba(0,0,0,0.5)' }}>

            {/* Top bar */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-green-900/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center justify-center">
                  <Radio size={20} className="text-green-400" />
                </div>
                <div>
                  <div className="text-xs font-mono text-green-700 tracking-widest">UMBRELLA CORP</div>
                  <div className="text-sm font-mono text-green-400 tracking-wider">TRC-4500 TACTICAL RADIO</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <motion.div
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ duration: locked ? 2 : 0.5, repeat: Infinity }}
                  className={`w-3 h-3 rounded-full ${locked ? 'bg-green-500' : isClose ? 'bg-yellow-500' : 'bg-red-500'}`}
                  style={{ boxShadow: `0 0 10px ${locked ? 'rgba(34,197,94,0.8)' : isClose ? 'rgba(234,179,8,0.8)' : 'rgba(239,68,68,0.8)'}` }}
                />
                {locked || isClose ? <Volume2 size={18} className="text-green-500" /> : <VolumeX size={18} className="text-red-500/50" />}
              </div>
            </div>

            {/* Frequency display */}
            <div className="mb-6">
              <FrequencyDisplay frequency={frequency} locked={locked} isClose={isClose} />
            </div>

            {/* Waveform */}
            <div className="mb-6">
              <WaveformDisplay signalStrength={signalStrength} locked={locked} />
            </div>

            {/* Audio visualizer */}
            <div className="mb-6 bg-black/50 rounded-xl p-4 border border-green-900/20">
              <AudioVisualizer signalStrength={signalStrength} locked={locked} />
            </div>

            {/* Controls row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
              <div className="flex justify-center">
                <SignalMeter strength={signalStrength} locked={locked} />
              </div>
              <div className="md:col-span-2">
                <TuningDial frequency={frequency} onChange={setFrequency} disabled={locked} />
              </div>
            </div>
          </div>

          {/* Lock button */}
          {!locked && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <motion.button
                whileHover={canLock ? { scale: 1.02 } : {}}
                whileTap={canLock ? { scale: 0.98 } : {}}
                onClick={handleLock}
                disabled={!canLock}
                className={`relative px-12 py-4 rounded-xl font-mono text-sm tracking-[0.3em] uppercase transition-all overflow-hidden ${
                  canLock
                    ? 'bg-green-500/20 border-2 border-green-500 text-green-400 hover:bg-green-500/30'
                    : 'bg-gray-800/50 border-2 border-gray-700 text-gray-600 cursor-not-allowed'
                }`}
                style={canLock ? { boxShadow: '0 0 30px rgba(34,197,94,0.3)' } : {}}
              >
                {canLock && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-green-500/20 to-transparent"
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                )}
                <span className="relative flex items-center justify-center gap-3">
                  {canLock ? <Unlock size={18} /> : <Lock size={18} />}
                  Lock Signal
                </span>
              </motion.button>

              <p className={`mt-3 text-sm font-mono ${canLock ? 'text-green-400' : isClose ? 'text-yellow-400' : 'text-gray-500'}`}>
                {canLock ? '✓ Signal optimal - Ready to lock!' : isClose ? '◎ Getting closer...' : '○ Tune to find the signal'}
              </p>
            </motion.div>
          )}

          {/* Transmission panel */}
          {locked && !solved && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-black/80 rounded-2xl p-6 border border-green-500/30"
              style={{ boxShadow: '0 0 40px rgba(34,197,94,0.1)' }}
            >
              <div className="flex items-center gap-3 mb-4 pb-3 border-b border-green-500/20">
                <motion.div
                  animate={{ scale: [1, 1.3, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="w-3 h-3 rounded-full bg-green-500"
                  style={{ boxShadow: '0 0 15px rgba(34,197,94,0.8)' }}
                />
                <span className="text-sm font-mono text-green-400 tracking-widest">INCOMING TRANSMISSION</span>
                {revealedLines < MESSAGE_LINES.length && (
                  <motion.span
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                    className="text-xs font-mono text-green-600 ml-auto"
                  >
                    RECEIVING...
                  </motion.span>
                )}
              </div>

              <div className="space-y-3 min-h-[200px]">
                {MESSAGE_LINES.slice(0, revealedLines).map((line, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4 }}
                    className="flex items-start gap-3"
                  >
                    <span className="text-green-500 font-mono">▸</span>
                    <p className="text-gray-300 font-light leading-relaxed">{line}</p>
                  </motion.div>
                ))}

                {revealedLines < MESSAGE_LINES.length && (
                  <motion.span
                    animate={{ opacity: [1, 0, 1] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                    className="inline-block w-3 h-5 bg-green-500 ml-6"
                  />
                )}

                {revealedLines === MESSAGE_LINES.length && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="pt-4 mt-4 border-t border-green-500/20 text-center space-y-3"
                  >
                    <span className="text-xs font-mono text-green-500 tracking-[0.3em]">TRANSMISSION COMPLETE</span>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleProceed}
                      className="block mx-auto px-8 py-2.5 rounded font-orbitron text-sm tracking-widest text-black relative overflow-hidden"
                      style={{ background: 'linear-gradient(135deg, #22c55e, #00ff41)' }}
                    >
                      <span className="relative z-10">PROCEED</span>
                      <motion.div
                        className="absolute inset-0"
                        animate={{ x: ['-100%', '200%'] }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                        style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)', width: '50%' }}
                      />
                    </motion.button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}

          {/* Multi-phase success */}
          {solved && (
            <div className="text-center relative">
              {/* Phase 1: Green flash + shockwave */}
              <AnimatePresence>
                {successPhase >= 1 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative">
                    <motion.div
                      initial={{ scale: 0.3, opacity: 0.8 }}
                      animate={{ scale: 3, opacity: 0 }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                      className="absolute left-1/2 top-10 -translate-x-1/2 w-20 h-20 rounded-full border-2 border-green-400"
                    />
                    <motion.div
                      initial={{ opacity: 0.6 }}
                      animate={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className="absolute inset-0 bg-green-400/10 rounded-lg"
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Phase 2: Radio icon */}
              {successPhase >= 2 && (
                <div className="relative w-20 h-20 mx-auto mb-4">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0 border-2 border-dashed border-green-500/20 rounded-full"
                  />
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <div className="w-16 h-16 rounded-full bg-green-900/30 border-2 border-green-500 flex items-center justify-center"
                      style={{ boxShadow: '0 0 25px rgba(0,255,65,0.2)' }}>
                      <Radio size={28} className="text-green-400" />
                    </div>
                  </motion.div>
                </div>
              )}

              {/* Phase 3: SIGNAL LOCKED */}
              {successPhase >= 3 && (
                <motion.p
                  initial={{ letterSpacing: '2px', opacity: 0 }}
                  animate={{ letterSpacing: '5px', opacity: 1 }}
                  className="font-orbitron text-green-400 text-sm mb-3"
                >
                  SIGNAL LOCKED
                </motion.p>
              )}

              {/* Phase 4: Stats */}
              {successPhase >= 4 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-2"
                >
                  <div className="flex items-center justify-center gap-6 text-xs font-terminal">
                    <span className="text-green-500">SIGNAL <span className="text-green-300 font-orbitron">100%</span></span>
                    <span className="text-green-500">FREQUENCY <span className="text-green-300 font-orbitron">87.3 MHz</span></span>
                  </div>
                  <p className="text-gray-400 font-rajdhani">Signal Beacon acquired</p>
                </motion.div>
              )}
            </div>
          )}

          {showProceed && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 20 }}
              className="text-center mt-8 relative z-20"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onComplete?.()}
                className="inline-flex items-center gap-2 px-8 py-3 rounded font-orbitron text-sm tracking-widest text-black relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #22c55e, #00ff41)' }}
              >
                <span className="relative z-10">PROCEED</span>
                <motion.div
                  className="absolute inset-0"
                  animate={{ x: ['-100%', '200%'] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)', width: '50%' }}
                />
              </motion.button>
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Leon dialogues */}
      {showLeon && (
        <LeonDialogue
          message={RADIO_TUNING.leon.intro}
          onDismiss={handleLeonDismiss}
        />
      )}

      {showFinalLeon && (
        <LeonDialogue
          message={RADIO_TUNING.leon.complete}
          onDismiss={handleFinalDismiss}
        />
      )}
    </div>
  );
}
